(() => {
  class ArmoniaSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, name, fn) => target.addEventListener(name, fn, {signal:this.abort.signal});
      this.on = on;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.index = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.delay = Math.max(5000, Number(this.dataset.interval) || 7000);
      this.controls = this.querySelector('.ida27-controls');

      this.setupSpotlightRotators();

      if (this.slides.length < 2) {
        if (this.slides.length) this.slides[0].hidden = false;
        return;
      }
      this.controls.hidden = false;
      this.show(0);
      this.pauseLabel();
      on(this, 'click', e => {
        const button = e.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-prev')) this.show(this.index - 1, true);
        if (button.hasAttribute('data-next')) this.show(this.index + 1, true);
        if (button.hasAttribute('data-go')) this.show(Number(button.dataset.go), true);
        if (button.hasAttribute('data-pause')) {this.paused = !this.paused; this.pauseLabel(); this.schedule();}
      });
      on(this, 'keydown', e => {
        if (!this.controls.contains(e.target) || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        e.preventDefault();
        this.show(this.index + (e.key === 'ArrowRight' ? 1 : -1), true);
      });
      on(this, 'pointerenter', e => {if (e.pointerType !== 'touch') {this.hovered=true; this.schedule();}});
      on(this, 'pointerleave', () => {this.hovered=false; this.schedule();});
      on(this, 'focusin', () => {this.focused=true; this.schedule();});
      on(this, 'focusout', () => {queueMicrotask(() => {if(this.abort){this.focused=this.contains(document.activeElement);this.schedule();}});});
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => {
        if(this.motion.matches) this.paused=true;
        this.pauseLabel();
        this.schedule();
      });
      on(document, 'shopify:block:select', e => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === e.detail.blockId);
        if(index < 0) return;
        this.paused=true;this.pauseLabel();this.show(index,true);
      });
      let touch;
      on(this, 'touchstart', e => {touch={x:e.touches[0].clientX,y:e.touches[0].clientY};});
      on(this, 'touchend', e => {
        if(!touch) return;
        const dx=e.changedTouches[0].clientX-touch.x,dy=e.changedTouches[0].clientY-touch.y;
        if(Math.abs(dx)>65 && Math.abs(dx)>Math.abs(dy)*1.5) this.show(this.index+(dx<0?1:-1),true);
        touch=null;
      });
      if('IntersectionObserver' in window){
        this.observer=new IntersectionObserver(entries=>{
          this.visible=entries[0].isIntersecting;
          this.schedule();
        });
        this.observer.observe(this);
      }
      this.schedule();
    }

    cleanSpotlightBackgrounds() {
      this.spotlightRotators?.forEach((rotator) => {
        const current = rotator._spotlightCurrent || rotator._spotlightPrimary;
        const image = current?.querySelector('.ida27-product-shot img');
        if (image) this.prepareProductImage(image);
      });
    }

    prepareProductImage(image) {
      if (!image || image.dataset.bgPrepared === 'true') return;
      image.dataset.bgPrepared = 'true';

      const process = () => this.removeWhiteBackground(image);
      if (image.complete && image.naturalWidth) process();
      else image.addEventListener('load', process, { once: true });
    }

    removeWhiteBackground(image) {
      if (!image || image.dataset.bgCleaned === 'true') return;
      const src = image.currentSrc || image.src;
      if (!src || src.startsWith('data:')) {
        image.dataset.bgCleaned = 'true';
        return;
      }

      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const w = probe.naturalWidth;
          const h = probe.naturalHeight;
          if (!w || !h) throw new Error('empty image');

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(probe, 0, 0);
          const frame = context.getImageData(0, 0, w, h);
          const pixels = frame.data;
          const original = new Uint8ClampedArray(pixels);

          const samples = [];
          const stepX = Math.max(1, Math.floor(w / 28));
          const stepY = Math.max(1, Math.floor(h / 28));
          const addSample = (x, y) => {
            const i = (y * w + x) * 4;
            if (original[i + 3] < 16) return;
            samples.push([original[i], original[i + 1], original[i + 2]]);
          };
          for (let x = 0; x < w; x += stepX) { addSample(x, 0); addSample(x, h - 1); }
          for (let y = 0; y < h; y += stepY) { addSample(0, y); addSample(w - 1, y); }
          if (!samples.length) throw new Error('no border samples');

          samples.sort((a, b) => ((b[0] + b[1] + b[2]) - (a[0] + a[1] + a[2])));
          const keep = samples.slice(0, Math.max(6, Math.ceil(samples.length * 0.62)));
          const bg = keep.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]], [0, 0, 0])
            .map((v) => v / keep.length);
          const bgLuma = (bg[0] + bg[1] + bg[2]) / 3;
          const bgSpread = Math.max(...bg) - Math.min(...bg);

          if (bgLuma < 184 || bgSpread > 58) {
            image.dataset.bgCleaned = 'not-white';
            image.classList.add('ida27-bg-fallback');
            return;
          }

          const colorDistance = (i) => {
            const dr = original[i] - bg[0];
            const dg = original[i + 1] - bg[1];
            const db = original[i + 2] - bg[2];
            return Math.sqrt(dr * dr + dg * dg + db * db);
          };

          const localGradient = (x, y) => {
            const i = (y * w + x) * 4;
            const r = original[i], g = original[i + 1], b = original[i + 2];
            let peak = 0;
            const neighbours = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]];
            for (const [nx, ny] of neighbours) {
              if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
              const ni = (ny * w + nx) * 4;
              const d = Math.abs(r - original[ni]) + Math.abs(g - original[ni + 1]) + Math.abs(b - original[ni + 2]);
              peak = Math.max(peak, d / 3);
            }
            return peak;
          };

          const visited = new Uint8Array(w * h);
          const stack = [];
          const seed = (x, y) => {
            if (x < 0 || y < 0 || x >= w || y >= h) return;
            const p = y * w + x;
            if (visited[p]) return;
            visited[p] = 1;
            stack.push(p);
          };
          for (let x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
          for (let y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }

          const hardThreshold = Math.max(24, Math.min(42, 28 + (255 - bgLuma) * 0.22));
          const softBand = 18;
          const maxGradient = 20;

          while (stack.length) {
            const p = stack.pop();
            const i = p * 4;
            const a = original[i + 3];
            if (!a) continue;

            const x = p % w;
            const y = (p - x) / w;
            const r = original[i], g = original[i + 1], b = original[i + 2];
            const luma = (r + g + b) / 3;
            const spread = Math.max(r, g, b) - Math.min(r, g, b);
            const distance = colorDistance(i);
            const gradient = localGradient(x, y);

            const paleEnough = luma > Math.max(196, bgLuma - 38) && spread < Math.max(46, bgSpread + 18);
            const smoothBackdrop = gradient <= maxGradient;
            if (!paleEnough || !smoothBackdrop || distance > hardThreshold + softBand) continue;

            if (distance <= hardThreshold) pixels[i + 3] = 0;
            else {
              const keepAlpha = (distance - hardThreshold) / softBand;
              pixels[i + 3] = Math.round(a * Math.max(.14, Math.min(1, keepAlpha)));
            }

            seed(x - 1, y); seed(x + 1, y); seed(x, y - 1); seed(x, y + 1);
          }

          context.putImageData(frame, 0, 0);
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = canvas.toDataURL('image/png');
          image.classList.add('ida27-bg-cleaned');
          image.dataset.bgCleaned = 'true';
        } catch (error) {
          image.dataset.bgCleaned = 'failed';
          image.classList.add('ida27-bg-fallback');
        }
      };
      probe.onerror = () => {
        image.dataset.bgCleaned = 'failed';
        image.classList.add('ida27-bg-fallback');
      };
      probe.src = src;
    }

    setupSpotlightRotators() {
      this.spotlightRotators = [...this.querySelectorAll('[data-spotlight-rotator]')];
      this.spotlightRotators.forEach(rotator => {
        const cards = [...rotator.querySelectorAll('[data-spotlight-card]')];
        if (!cards.length) {rotator.hidden = true; return;}
        rotator._cards = cards;
        rotator._index = 0;
        const dots = rotator.querySelector('[data-bs-dots]');
        dots.replaceChildren();
        cards.forEach((card, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `Mostra ${card.dataset.title}`);
          this.on(button, 'click', () => select(index, true));
          dots.append(button);
        });
        const select = (index, announce = false) => {
          rotator._index = (index + cards.length) % cards.length;
          cards.forEach((card, i) => {card.hidden = i !== rotator._index;});
          [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === rotator._index)));
          rotator._spotlightCurrent = cards[rotator._index];
          rotator.querySelector('[data-bs-count]').textContent = `${String(rotator._index+1).padStart(2,'0')} / ${String(cards.length).padStart(2,'0')}`;
          if (announce) rotator.querySelector('[data-bs-status]').textContent = cards[rotator._index].dataset.title;
          this.cleanSpotlightBackgrounds();
        };
        rotator.querySelector('.ida30-nav').hidden = cards.length < 2;
        this.on(rotator.querySelector('[data-bs-prev]'), 'click', () => select(rotator._index-1, true));
        this.on(rotator.querySelector('[data-bs-next]'), 'click', () => select(rotator._index+1, true));
        this.on(rotator, 'keydown', e => {
          if (!['ArrowLeft','ArrowRight'].includes(e.key)) return;
          e.preventDefault();e.stopPropagation();select(rotator._index+(e.key==='ArrowRight'?1:-1), true);
        });
        let touch;
        this.on(rotator, 'touchstart', e => {e.stopPropagation();touch={x:e.touches[0].clientX,y:e.touches[0].clientY};});
        this.on(rotator, 'touchend', e => {
          e.stopPropagation();if(!touch)return;
          const dx=e.changedTouches[0].clientX-touch.x, dy=e.changedTouches[0].clientY-touch.y;
          if(Math.abs(dx)>45 && Math.abs(dx)>Math.abs(dy)*1.5)select(rotator._index+(dx<0?1:-1), true);
          touch=null;
        });
        rotator.dataset.bsReady = "true";
        select(0);
      });
    }

    chooseSpotlightForHeroEntry(rotator) {
      const image = rotator._spotlightCurrent?.querySelector('.ida27-product-shot img');
      if(image) this.prepareProductImage(image);
    }

    show(index, announce=false) {
      const nextIndex=(index+this.slides.length)%this.slides.length;
      const changed=nextIndex!==this.index || !this._hasShownSlide;
      this.index=nextIndex;
      this.slides.forEach((slide,i)=>{slide.hidden=i!==this.index;});
      this.dots.forEach((dot,i)=>dot.setAttribute('aria-current',String(i===this.index)));
      if (changed) {
        const activeSlide=this.slides[this.index];
        activeSlide?.querySelectorAll('[data-spotlight-rotator]').forEach(rotator => this.chooseSpotlightForHeroEntry(rotator));
      }
      this._hasShownSlide=true;
      if(announce) this.querySelector('[data-status]').textContent=`Slide ${this.index+1} di ${this.slides.length}`;
      this.schedule();
    }
    pauseLabel() {
      const button=this.querySelector('[data-pause]');
      if (!button) return;
      button.setAttribute('aria-label',this.paused?'Avvia la rotazione delle slide':'Metti in pausa le slide');
      const label=button.querySelector('[data-pause-label]');
      if(label) label.textContent=this.paused?'Riproduci':'Pausa';
    }
    schedule() {
      clearTimeout(this.timer);
      if(!this.abort || this.paused || this.hovered || this.focused || !this.visible || document.hidden || this.slides.length<2) return;
      this.timer=setTimeout(()=>this.show(this.index+1),this.delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();this.abort=null;this.observer?.disconnect();
    }
  }
  if (!customElements.get('armonia-slideshow')) customElements.define('armonia-slideshow',ArmoniaSlideshow);

  class ArmoniaMaisonLogos extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.querySelectorAll('.ida27-brand-card__logo img').forEach(image => this.prepareLogo(image));
    }

    prepareLogo(image) {
      if (!image || image.dataset.logoPrepared === 'true') return;
      image.dataset.logoPrepared = 'true';
      const process = () => this.removeLightLogoBackground(image);
      if (image.complete && image.naturalWidth) process();
      else image.addEventListener('load', process, {once:true});
    }

    removeLightLogoBackground(image) {
      const src = image.currentSrc || image.src;
      if (!src || src.startsWith('data:')) return;
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = probe.naturalWidth;
          canvas.height = probe.naturalHeight;
          const ctx = canvas.getContext('2d', {willReadFrequently:true});
          ctx.drawImage(probe,0,0);
          const frame = ctx.getImageData(0,0,canvas.width,canvas.height);
          const px = frame.data;
          const w=canvas.width,h=canvas.height;
          const sample=(x,y)=>{const i=(y*w+x)*4;return [px[i],px[i+1],px[i+2],px[i+3]]};
          const corners=[sample(0,0),sample(w-1,0),sample(0,h-1),sample(w-1,h-1)];
          const isLight=c=>c[3]>220 && ((c[0]+c[1]+c[2])/3)>225 && (Math.max(c[0],c[1],c[2])-Math.min(c[0],c[1],c[2]))<32;
          if (corners.filter(isLight).length < 3) return;
          for (let i=0;i<px.length;i+=4) {
            const r=px[i],g=px[i+1],b=px[i+2],a=px[i+3];
            if (!a) continue;
            const avg=(r+g+b)/3, spread=Math.max(r,g,b)-Math.min(r,g,b);
            if (avg>246 && spread<24) px[i+3]=0;
            else if (avg>230 && spread<30) px[i+3]=Math.round(a*Math.max(0,Math.min(1,(246-avg)/16)));
          }
          ctx.putImageData(frame,0,0);
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src=canvas.toDataURL('image/png');
          image.dataset.logoCleaned='true';
        } catch (_) {}
      };
      probe.src=src;
    }
  }

  if (!customElements.get('armonia-maison-logos')) customElements.define('armonia-maison-logos',ArmoniaMaisonLogos);
})();
